<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="cg-question">
        <span class="cg-bar cg-bar--xl"></span>
      </div>
      <div class="cg-buckets">
        <div
          v-for="(b, i) in buckets"
          :key="b.label"
          class="cg-bucket"
          :style="{ '--b-i': i }"
        >
          <span class="cg-bucket-label">{{ b.label }}</span>
          <div class="cg-bucket-body">
            <span
              v-for="n in b.count"
              :key="n"
              class="cg-chip"
              :style="{ '--chip-i': n - 1 }"
            ></span>
          </div>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="cg-phone-q"></span>
      <div class="cg-phone-stage">
        <div class="cg-phone-item" :style="{ '--accent': slide.accent }">
          <span class="cg-bar cg-bar--md"></span>
        </div>
        <span class="material-symbols-rounded cg-phone-arrow">arrow_downward</span>
        <div class="cg-phone-buckets">
          <span
            v-for="i in 3"
            :key="i"
            class="cg-phone-bucket"
            :class="{ '--target': i === 2 }"
          ></span>
        </div>
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewCategorise',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      buckets: [
        { label: 'Citrus', count: 3 },
        { label: 'Berry', count: 4 },
        { label: 'Stone', count: 2 },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.cg-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
}
.cg-bar--xl { width: 70%; }
.cg-bar--md { width: 50%; }

.cg-question {
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.cg-buckets {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  flex: 1;
}
.cg-bucket {
  background: color-mix(in srgb, var(--accent) 6%, white);
  border: 1.5px dashed color-mix(in srgb, var(--accent) 35%, transparent);
  border-radius: 8px;
  padding: 8px;
  display: flex; flex-direction: column; gap: 6px;
  min-height: 90px;
}
.cg-bucket-label {
  font-size: 10px; font-weight: 800;
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.cg-bucket-body {
  display: flex; flex-wrap: wrap; gap: 4px;
  align-content: flex-start;
}
.cg-chip {
  width: 18px; height: 18px;
  border-radius: 4px;
  background: var(--accent);
  opacity: 0;
  transform: scale(0.5);
  animation: cg-drop 4s ease-out infinite;
  animation-delay: calc((var(--b-i, 0) * 3 + var(--chip-i, 0)) * 200ms + 400ms);
}

/* ---- Audience phone ---- */
.cg-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.cg-phone-stage {
  flex: 1;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 4px 0;
}
.cg-phone-item {
  background: white;
  border: 1.5px solid var(--accent);
  border-radius: 8px;
  padding: 8px 12px;
  width: 80%;
  box-shadow: 0 4px 8px -2px color-mix(in srgb, var(--accent) 30%, transparent);
  animation: cg-drag 4s ease-in-out infinite;
}
.cg-phone-arrow {
  font-size: 16px; color: var(--accent);
  animation: cg-bounce 4s ease-in-out infinite;
}
.cg-phone-buckets {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 4px;
  width: 100%;
}
.cg-phone-bucket {
  height: 28px;
  border-radius: 6px;
  border: 1.5px dashed rgba(15, 15, 20, 0.18);
  background: rgba(15, 15, 20, 0.03);
}
.cg-phone-bucket.--target {
  border-color: var(--accent);
  background: color-mix(in srgb, var(--accent) 14%, white);
  animation: cg-pulse 4s ease-in-out infinite;
}

@keyframes cg-drop {
  0%, 8%   { opacity: 0; transform: scale(0.5) translateY(-12px); }
  35%      { opacity: 1; transform: scale(1) translateY(0); }
  92%, 100% { opacity: 1; transform: scale(1); }
}
@keyframes cg-drag {
  0%, 100% { transform: translateX(0); }
  50%      { transform: translateX(8px); }
}
@keyframes cg-bounce {
  0%, 100% { transform: translateY(0); opacity: 0.4; }
  50%      { transform: translateY(2px); opacity: 1; }
}
@keyframes cg-pulse {
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  50%      { box-shadow: 0 0 0 4px color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .cg-chip, .cg-phone-item, .cg-phone-arrow, .cg-phone-bucket.--target {
    animation: none;
    opacity: 1;
    transform: none;
  }
}
</style>
